import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { choosableModels, fixedModel, modelPickerMode } from '../lib/donationModels';
import type { DonationRow, ModelOption } from '../lib/donations';
import { DonationModelSelect } from './DonationModelSelect';
import { Field } from './Field';

// Which model a donated inference key runs (arc 101 slice 11, plan doc 05) — the
// maintainer's one steering decision over someone else's key.
//
// It renders the shape `modelPickerMode` derives from the DONOR's ceiling and
// decides nothing itself:
//
//   | Ceiling | Renders |
//   |---|---|
//   | absent (money/compute, or a key that is gone) | nothing |
//   | one model | static text — a one-option dropdown asks for a choice that does not exist |
//   | a concrete list | exactly those options, no free text |
//   | `*` | the platform catalog PLUS free text for a `custom` endpoint |
//
// The ceiling is the donor's authorization and stays server-side: a pick outside
// it is a 422 naming what is permitted, and the pool resolver re-applies it when
// the credential is sealed. This only keeps a maintainer from being offered a
// model that would plainly be refused.

export type DonationModelPickerProps = {
  readonly row: DonationRow;
  /** The platform catalog off the received page — spent only on an open ceiling. */
  readonly catalog: readonly ModelOption[];
  /** Whether this caller may write at all (the `canManageDonations` mirror). */
  readonly offered: boolean;
  readonly busy: boolean;
  readonly onChoose: (model: string | null) => void;
};

export function DonationModelPicker(props: DonationModelPickerProps): JSX.Element {
  const mode = () => modelPickerMode(props.row.allowed_models, props.catalog);

  return (
    <>
      <Show when={fixedModel(mode())}>
        {(model) => (
          <Field
            label={t('donations.model_heading')}
            id={`donation-model-${props.row.id}`}
            desc={t('donations.model_fixed_note')}
          >
            {/* A ceiling of one model is a read-out, not a choice: an <output>,
                which the label names. */}
            {(a11y) => (
              <output {...a11y} class="data-table__mono">
                {model()}
              </output>
            )}
          </Field>
        )}
      </Show>
      <Show when={choosableModels(mode())}>
        {(choice) => (
          <DonationModelSelect
            row={props.row}
            models={choice().models}
            freeText={choice().freeText}
            offered={props.offered}
            busy={props.busy}
            onChoose={props.onChoose}
          />
        )}
      </Show>
    </>
  );
}
